/**
 * EAI-1598 — presentation metadata for the user Notification Preferences tab.
 *
 * The backend `getCompanyNudgeEvent` query returns EVERY active nudge event for
 * the company. This registry decides which of them are user-configurable, how
 * they're grouped, labeled, and which control they render — so the UI is
 * data-driven, not hard-coded per code.
 *
 * SCALING:
 *   * Add a new OttoVin (or any) user-facing nudge -> add one entry to
 *     NUDGE_PREFS_CONFIG. No structural/component change.
 *   * Add a whole new area (general EngagedAi notifications) -> add a new
 *     CATEGORY + its entries. The tab groups by category automatically.
 *   * Any nudge code returned by the query that is NOT in this registry is
 *     skipped (kept out of the user tab) — internal/appointment/manager
 *     reminders stay admin-only.
 *
 * BACKEND-READY: when NudgeEvent gains `category` / `display_name` /
 * `description` / `control_type` / `is_user_configurable` columns, this file
 * can be replaced by (or fall back to) those server values with minimal change
 * — the component only depends on the shape returned by getNudgePrefConfig().
 */

// Control types a nudge row can render.
export const CONTROL_TYPE = {
  // Simple on/off toggle (+ channels + optional template).
  TOGGLE: "toggle",
  // 3-way frequency segmented control (Off / First / Every) — e.g. link opens.
  FREQUENCY: "frequency",
};

// Categories (groups) shown as sections, in this order.
export const NUDGE_CATEGORIES = [
  {
    key: "ottovin",
    title: "OttoVin",
    description: "Alerts for the OttoVin links you send to customers.",
  },
  // Future: { key: "messages", title: "Messages", description: "…" }, etc.
];

// Insertable template variables, per category (so future categories can offer
// their own variable sets). Keyed by category key.
export const TEMPLATE_VARIABLES_BY_CATEGORY = {
  ottovin: [
    { token: "{leadName}", hint: "Customer's name" },
    { token: "{ottovinVehicle}", hint: "Year / make / model of the vehicle" },
    { token: "{ottovinVehicleLink}", hint: "Shortened link to the vehicle page" },
  ],
};

/**
 * The registry, keyed by NudgeEvent.code. Each entry:
 *   category        - which NUDGE_CATEGORIES group it belongs to
 *   label           - user-facing row label (falls back to NudgeEvent.title)
 *   description      - optional helper text under the label
 *   controlType     - CONTROL_TYPE.TOGGLE | FREQUENCY
 *   defaultTemplate - pre-filled message + "reset to default" target
 *   editableTemplate- whether to show the message editor (default true)
 *   sortOrder       - order within its category
 */
export const NUDGE_PREFS_CONFIG = {
  OTTOVIN_VEHICLE_VIEWED: {
    category: "ottovin",
    label: "Link opens",
    description: "How often to notify you when a customer opens an OttoVin link you sent.",
    controlType: CONTROL_TYPE.FREQUENCY,
    defaultTemplate:
      "{leadName} just viewed a {ottovinVehicle} on OttoVIN and is actively browsing. " +
      "Reach out now while they're engaged: {ottovinVehicleLink}",
    editableTemplate: true,
    sortOrder: 1,
  },
  OTTOVIN_CHAT_STARTED: {
    category: "ottovin",
    label: "Customer starts a chat with Otto",
    controlType: CONTROL_TYPE.TOGGLE,
    defaultTemplate:
      "{leadName} just started a chat on OttoVIN about a {ottovinVehicle}. " +
      "Jump in and connect with them right away: {ottovinVehicleLink}",
    editableTemplate: true,
    sortOrder: 2,
  },
  OTTOVIN_CALL_TO_ACTION_CLICKED: {
    category: "ottovin",
    label: "Customer clicks a call-to-action button",
    controlType: CONTROL_TYPE.TOGGLE,
    defaultTemplate:
      "{leadName} just clicked a call-to-action on OttoVIN for a {ottovinVehicle}. " +
      "This is a strong buying signal. Reach out now to move them forward: {ottovinVehicleLink}",
    editableTemplate: true,
    sortOrder: 3,
  },
};

/**
 * Returns the presentation config for a nudge code, or null if the code is not
 * user-configurable (so the component skips it). Single lookup point — swap the
 * body for a server-driven source later without touching the component.
 */
export const getNudgePrefConfig = code => NUDGE_PREFS_CONFIG[code] || null;
